<script setup lang="ts">
import { ref, onMounted } from 'vue';
import CountriesRepository from '../../repositories/countries.repository';
import Store from '@store/dataProvider';
import { storeToRefs } from 'pinia';
import { IPagination } from '../../types/pagination.type';
import { ICountry } from '../../types/countries.type';
import { useToast } from '../../composables/useToast';
import { useI18n } from 'vue-i18n';
import DataTable from '@components/DataTable.vue';

const isLoadingCountries = ref<boolean>(false);

const { t } = useI18n();
const store = Store();
const { $toast } = useToast();
const { countries: countriesPinia }: any = storeToRefs(store);
const countries = ref<ICountry[]>([]);
const currentPage = ref<number>();
const paginationData = ref<IPagination>();
const isViewActive = ref<boolean>(true);
const fieldsTableInactive: any = ref([
   {
    key: 'id',
    label: t('global.id'),
    sortable: true,
  },
  {
    key: 'description',
    label: t('global.pais'),
  },
  {
    key: 'iso2',
    label: 'ISO2',
  },
  {
    key: 'iso3',
    label: 'ISO3',
  },
  {
    key: 'countryInfo',
    label: t('global.estado'),
  },
]);

const fieldsTableActive: any = ref([
  {
    key: 'id',
    label: t('global.id'),
    sortable: true,
  },
  {
    key: 'description',
    label: t('global.pais'),
  },
  {
    key: 'iso2',
    label: 'ISO2',
  },
  {
    key: 'iso3',
    label: 'ISO3',
  },
  {
    key: 'countryInfo',
    label: t('global.estado'),
  },
]);

// Load Countries ABM
const getCountriesFromAPI = async (page: number, blocked: boolean = false) => {
  isLoadingCountries.value = true;
  try {
    const { data } = await CountriesRepository.getcountries({ page, blocked });
    if (data && Array.isArray(data?.countries)) {
      paginationData.value = data.pagination;
      updateCountries(data.countries);
    }
    return [];
  } catch (error) {
    console.error('Error while fetching countries:', error);
    return [];
  } finally {
    isLoadingCountries.value = false;
  }
};

const updateCountries = (newCountries: ICountry[]) => {
  countries.value = newCountries;
  store.setProp({ key: 'countries', value: newCountries });
};

const loadCountries = async () => {
  await getCountriesFromAPI(1);
};

onMounted(loadCountries);

// UI
const onChangePage = async (page: number) => {
  getCountriesFromAPI(page, !isViewActive.value);
  currentPage.value = page;
};

const handleUpdateCountry = async(countryData: ICountry) => {
  try {
    const data = await CountriesRepository.updateCountry({...countryData, blocked: !countryData.blocked});
    let index = store.countries.findIndex((value: any) => value.id === data.id);
    if (index > -1) {
		  store.deleteItem({
          key: 'countries',
          value: data.id,
        });
    }
    $toast?.success(t('ajustes.paises.pais_actualizado_con_exito'));
  } catch (error) {
    $toast?.error(t('global.ocurrio_un_error'));
  }
};

const handleActive = async () => {
  await getCountriesFromAPI(1, );
  isViewActive.value = true;
};

const handleInactive = async () => {
  await getCountriesFromAPI(1, true);
  isViewActive.value = false;
};
</script>

<template>
  <Layout :title="t('ajustes.paises.paises')" :textColor="'color-secondary'">
    <template #layout>
	 <div class="d-flex justify-content-between">
        <div>
          <CButton
            class="background-blue button__form"
            style="padding: 0.5rem !important; margin-right: 15px"
            type="button"
            @click="handleActive"
          >
            <template #content>{{ t('global.activos') }}</template>
          </CButton>
          <CButton
            class="background-pink button__form"
            style="padding: 0.5rem !important"
            type="button"
            @click="handleInactive"
          >
            <template #content>{{ t('global.inactivos') }}</template>
          </CButton>
        </div>
      </div>

      <DataTable
           :loading="isLoadingCountries"
           :fields="isViewActive ? fieldsTableActive : fieldsTableInactive"
        :items="countriesPinia"
        @update:model-value-page="onChangePage"
        :totalRows="paginationData?.count"
        :perPage="20"
        :currentPage="paginationData?.current_page"
      >
        <template v-slot:cell(countryInfo)="countryData">
          <div
            @click="handleUpdateCountry(countryData.data.item)"
            class="button-icon"
          >
            <img
              class="cursor-pointer"
              :src="
                countryData.data.item.blocked
                  ? '/icons/cross-circle.svg'
                  : '/icons/check-circle.svg'
              "
              style="width: 40px"
            />
          </div>
        </template>
      </DataTable>
    </template>
  </Layout>
</template>
